import { actions } from './reducer'
import ssapi from 'lib/api/secondstepApi'
import docServiceApi from 'lib/DocumentServiceApi'
import { push } from 'connected-react-router'
import { addNotification } from 'store/notifications'
import { getReadableErrorMessage } from 'utils/errorUtils'

export const fetchAndHandleArchetypes = () => {
  return async function (dispatch, getState) {
    const state = getState()
    const { userManager } = state
    if (userManager.archetypes.data) {
      return true
    }
    dispatch(actions.fetchingArchetypes())

    try {
      const response = await ssapi.getArchetypes()
      const data = response.data
      if (response.status === 200) {
        dispatch(actions.fetchingArchetypesSuccess(data))
      }
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Failed to fetch user archetypes.',
      )
      dispatch(actions.fetchingArchetypesError(error))
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
      return false
    }
  }
}

export const fetchAndHandleProductUser = (email, id) => {
  return async function (dispatch) {
    dispatch(actions.fetchingProductUser())

    try {
      const response = await ssapi.getUserDetails(parseInt(id, 10))
      if (response.status === 204) {
        dispatch(actions.fetchingProductUserSuccess(null))
        dispatch(
          push({
            pathname: '/css-admin/users',
            state: { email: email, id: id, notFound: true },
          }),
        )
      } else {
        dispatch(actions.fetchingProductUserSuccess(response.data))
        dispatch(push(`/css-admin/users/${email}/${id}`))
      }
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Error fetching Product User',
      )
      dispatch(actions.fetchingProductUserError(errorMessage))
      return false
    }
  }
}

export const fetchSetupAdminUserId = setupAdminEmail => {
  return async dispatch => {
    dispatch(actions.fetchingSetupAdminUserId())
    try {
      const response = await ssapi.getSetupAdminUserId(setupAdminEmail)
      if (!!response.data && response.data.length > 0) {
        const setupAdmin = response.data[0]
        const { id = null } = setupAdmin || {}
        dispatch(actions.fetchingSetupAdminUserIdSuccess(id))
        return id // Return the user ID for immediate use
      } else {
        dispatch(actions.fetchingSetupAdminUserIdError('No user found'))
        return null
      }
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Error fetching Setup Admin User',
      )
      dispatch(actions.fetchingSetupAdminUserIdError(errorMessage))
      return false
    }
  }
}

export const fetchNetsuiteCustomer = netsuiteCustomerId => {
  return async dispatch => {
    dispatch(actions.fetchingNetsuiteCustomer())

    try {
      const response = await ssapi.getNetSuiteCustomers({
        netSuiteCustomerId: netsuiteCustomerId,
      })
      dispatch(actions.fetchingNetsuiteCustomerSuccess(response.data[0]))
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Failed to fetch NetSuite customer.',
      )
      dispatch(actions.fetchingNetsuiteCustomerError(errorMessage))
      return false
    }
  }
}

export const saveAndHandleProductUser = productUser => {
  return async function (dispatch) {
    dispatch(actions.savingProductUser())

    try {
      const response = await ssapi.saveProductUser(productUser)
      dispatch(actions.savingProductUserSuccess(response.data))
      dispatch(
        addNotification({
          color: 'success',
          text: 'Successfully saved user.',
        }),
      )
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Failed to save user.',
      )
      dispatch(actions.savingProductUserError(errorMessage))
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
    }
  }
}

export const removePakFromProductUser = pakNo => {
  return async function (dispatch, getState) {
    dispatch(actions.savingProductUser())
    const productUser = getState().userManager.productUser

    try {
      const response = await ssapi.removePakFromProductUser(
        productUser.email,
        pakNo,
      )
      dispatch(actions.savingProductUserSuccess(response.data))
      dispatch(
        addNotification({
          color: 'success',
          text: 'Successfully removed pak.',
        }),
      )
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Failed to remove pak.',
      )
      dispatch(actions.savingProductUserError(errorMessage))
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
    }
  }
}

export const addPakToProductUser = pakNo => {
  return async function (dispatch, getState) {
    const productUser = getState().userManager.productUser

    dispatch(actions.savingProductUser())
    try {
      await ssapi.addPakToProductUser(pakNo, productUser.id)
      dispatch(actions.savingProductUserPakSuccess())
      dispatch(
        addNotification({
          color: 'success',
          text: 'Successfully added pak.',
        }),
      )

      // not ideal for updating PAK grid, will fix when api give a response
      const { id, email } = productUser || {}
      if (!!email && !!id) {
        dispatch(fetchAndHandleProductUser(email, id))
      }
      return true
    } catch (error) {
      const errorMessage = getReadableErrorMessage(error, 'Failed to add pak.')
      dispatch(actions.savingProductUserError(errorMessage))
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
    }
  }
}

export const fetchLessonProgressReport = (userEmail, programCode) => {
  return async dispatch => {
    dispatch(actions.fetchingLessonProgressReport())
    try {
      const response = await docServiceApi.post('/SiteReport', {
        userEmail,
        program: programCode,
        options: {
          useFlexTemplate: true,
          useHsFullTemplate: true,
        },
      })
      const sasUrl = response.data.sasUrl
      dispatch(actions.fetchingLessonProgressReportSuccess(sasUrl))
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        `Failed to get Lesson Progress Report for program ${programCode}`,
      )
      dispatch(actions.fetchingLessonProgressReportError(error))
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
    }
  }
}

export const clearUserCache = (userId: number) => {
  return async dispatch => {
    try {
      await ssapi.clearCache({ userId })
      dispatch(
        addNotification({
          color: 'success',
          text: 'User cache cleared successfully.',
        }),
      )
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Failed to clear user cache.',
      )
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
    }
  }
}

export const clearLessonProgressReportLink = () => {
  return async dispatch => {
    dispatch(actions.fetchingLessonProgressReportSuccess(null))
  }
}

export const clearUsers = () => {
  return async function (dispatch) {
    dispatch(actions.clearUsers())
  }
}
